import cn from "classnames";
import { format } from "date-fns";
import Markdown from "markdown-to-jsx";
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { Form, Toast } from "react-bootstrap";
import { MdCheckCircle, MdClose, MdContentCopy } from "react-icons/md";
import {
  LLMFeedbackType,
  LLMMessage,
  SubmitFeedbackArgs
} from "@blisspointmedia/bpm-types/dist/LLM";
import { capitalizeString } from "@blisspointmedia/bpm-types/dist/StyleUtils";
import { Button, ButtonType } from "../../Components/Button/Button";
import { ButtonFrameworkVariant } from "../../Components/ButtonFramework/ButtonFramework";
import { Success700 } from "../../utils/colors";
import { MiscLambdaFetch } from "../../utils/fetch-utils";
import { Mixpanel, MxE } from "../../utils/mixpanelWrapper";
import { getFeedbackIcon, NEGATIVE_FEEDBACK_OPTIONS } from "../LLMConstants";

interface FeedbackButtonProps {
  type: LLMFeedbackType;
  msgFeedbackState: LLMFeedbackType | null;
  submitFeedback: () => void;
  tooltipText: string;
}

const FeedbackButton = ({
  type,
  msgFeedbackState,
  submitFeedback,
  tooltipText,
}: FeedbackButtonProps) => (
  <Button
    className={cn(
      `messageAction messageActionFeedback messageActionFeedback${capitalizeString(type)}`,
      {
        active: type === msgFeedbackState,
      }
    )}
    type={ButtonType.EMPTY}
    variant={ButtonFrameworkVariant.ICON_ONLY}
    design="secondary"
    icon={getFeedbackIcon(type, type === msgFeedbackState)}
    onClick={submitFeedback}
    tooltipText={tooltipText}
    tooltipClass="llmButtonTooltip"
  />
);

interface LLMMessageBoxProps {
  message: LLMMessage;
  isLastMessage: boolean;
  scrollRef: MutableRefObject<HTMLDivElement | null>;
  // Timestamp of the previous message in the conversation, or null if this is the first message
  prevTimestamp: number | null;
  setMessageFeedback: (newMsg: Partial<LLMMessage>) => void;
}

export const LLMMessageBox = ({
  message,
  isLastMessage,
  scrollRef,
  prevTimestamp,
  setMessageFeedback,
}: LLMMessageBoxProps): JSX.Element => {
  const hoverRef = useRef<HTMLDivElement | null>(null);
  const feedbackScrollRef = useRef<HTMLDivElement | null>(null);
  const [messageHovered, setMessageHovered] = useState<boolean>(false);
  const [showFeedbackReasonForm, setShowFeedbackReasonForm] = useState<boolean>(false);
  const [showFeedbackDetailsInput, setShowFeedbackDetailsInput] = useState<boolean>(false);
  const [feedbackDetails, setFeedbackDetails] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showFeedbackSuccessToast, setShowFeedbackSuccessToast] = useState<boolean>(false);

  const { messageId, type, contents, feedback, feedbackReason, timestamp } = message;
  const showActions = isLastMessage || messageHovered;

  const thisDate = useMemo(() => new Date(timestamp), [timestamp]);
  const prettyTime = useMemo(() => format(thisDate, "h:mma"), [thisDate]);
  const prettyDate: string | null = useMemo(() => {
    // If this is the first message, chats from days other than today should show the date
    // Otherwise, only show the date if it's different from the last message
    const comparisonDate = prevTimestamp ? new Date(prevTimestamp) : new Date();

    if (
      comparisonDate.getDate() === thisDate.getDate() &&
      comparisonDate.getMonth() === thisDate.getMonth() &&
      comparisonDate.getFullYear() === thisDate.getFullYear()
    ) {
      return null;
    }

    return format(thisDate, "MMMM d, Y");
  }, [prevTimestamp, thisDate]);

  const copyMessage = () => {
    Mixpanel.track(MxE.LLM_COPY_MESSAGE, { messageId });
    navigator.clipboard.writeText(contents);
  };

  const submitFeedback = async ({
    type,
    reason,
    shouldPromptForReason = false,
    shouldShowSuccessToast = true,
  }: {
    type: LLMFeedbackType;
    reason?: string;
    shouldPromptForReason?: boolean;
    shouldShowSuccessToast?: boolean;
  }) => {
    const submitToLambdas = async (type, reason) => {
      const body: SubmitFeedbackArgs = {
        messageId,
        type,
        reason,
      };

      if (!submitting) {
        setSubmitting(true);
        await MiscLambdaFetch("/submitLLMFeedback", {
          method: "POST",
          body,
        });

        if (shouldShowSuccessToast) {
          setShowFeedbackSuccessToast(true);
        }
        setSubmitting(false);
      }
    };

    setShowFeedbackReasonForm(shouldPromptForReason);

    if (type !== feedback || reason !== feedbackReason) {
      setMessageFeedback({ feedback: type, feedbackReason: reason ?? null });
      await submitToLambdas(type, reason);
    }
  };

  useEffect(() => {
    if (showFeedbackReasonForm || showFeedbackDetailsInput) {
      feedbackScrollRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [showFeedbackDetailsInput, showFeedbackReasonForm]);

  return (
    <>
      {prettyDate && (
        <div className="messageDate">
          {prettyDate} • {prettyTime}
        </div>
      )}
      <div className="messageAndFeedbackContainer" ref={isLastMessage ? scrollRef : undefined}>
        <div
          className={cn("messageContainer", {
            prompt: type === "PROMPT",
            response: type === "RESPONSE",
          })}
          ref={hoverRef}
          // TODO: Add delay
          onMouseEnter={() => setMessageHovered(true)}
          onMouseLeave={() => setMessageHovered(false)}
        >
          <div className="message">
            <Markdown>{contents}</Markdown>
          </div>
          <div className="messageFooter">
            <div className="messageTime">{prettyTime}</div>
            {showActions && (
              <div className="messageActions">
                <Button
                  className="messageAction messageActionCopy"
                  type={ButtonType.EMPTY}
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  design="secondary"
                  icon={<MdContentCopy />}
                  onClick={copyMessage}
                  tooltipText="Copy"
                  tooltipClass="llmButtonTooltip"
                />
                {type === "RESPONSE" && (
                  <>
                    <FeedbackButton
                      type="POSITIVE"
                      msgFeedbackState={feedback}
                      submitFeedback={() =>
                        submitFeedback({
                          type: "POSITIVE",
                        })
                      }
                      tooltipText="Like"
                    />
                    <FeedbackButton
                      type="NEGATIVE"
                      msgFeedbackState={feedback}
                      submitFeedback={() =>
                        submitFeedback({
                          type: "NEGATIVE",
                          shouldPromptForReason: true,
                          shouldShowSuccessToast: false,
                        })
                      }
                      tooltipText="Dislike"
                    />
                  </>
                )}
              </div>
            )}
            <Toast
              className="feedbackSuccessToast"
              show={showFeedbackSuccessToast}
              onClose={() => setShowFeedbackSuccessToast(false)}
              autohide
            >
              <div className="toastLeft">
                <MdCheckCircle color={Success700} />
                Feedback submitted
              </div>
              <MdClose
                className="close"
                onClick={() => setShowFeedbackSuccessToast(false)}
                color="#A4A4A4"
              />
            </Toast>
          </div>
        </div>
        {showFeedbackReasonForm && (
          <div className="feedbackReasonForm">
            <div className="feedbackQuestion">Why wasn't this response helpful?</div>
            {NEGATIVE_FEEDBACK_OPTIONS.map((option, index) => (
              <Form.Check
                key={`feedback-option-${index}`}
                type="radio"
                label={option}
                id={`feedback-option-${index}`}
                onClick={() => submitFeedback({ type: "NEGATIVE", reason: option })}
              />
            ))}
            <Form.Check
              key={"feedback-option-other"}
              type="radio"
              label={"Other"}
              id={"feedback-option-other"}
              onClick={() => {
                submitFeedback({
                  type: "NEGATIVE",
                  reason: "Other",
                  shouldPromptForReason: true,
                  shouldShowSuccessToast: false,
                });
                setShowFeedbackDetailsInput(true);
              }}
            />
            {showFeedbackDetailsInput && (
              <>
                <Form.Control
                  value={feedbackDetails}
                  onChange={e => setFeedbackDetails(e.target.value)}
                />
                <Button
                  onClick={() => {
                    submitFeedback({
                      type: "NEGATIVE",
                      reason: `Other: ${feedbackDetails}`,
                    });
                    setFeedbackDetails("");
                    setShowFeedbackDetailsInput(false);
                  }}
                >
                  Submit Feedback
                </Button>
              </>
            )}
          </div>
        )}
        <div ref={feedbackScrollRef} id="feedbackReasonFormScrollRef" />
      </div>
    </>
  );
};
